import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sourceApi } from './sourceApi';
import {
  isTerminalStatus,
  type BillingSummary,
  type IngestionStatusResponse,
  type ScrapeConfirmResponse,
  type ScrapeEstimateResult,
  type ScrapeEstimateStart,
  type ScrapeProgress,
  type SourceListItem,
  type SourcePagesResponse,
  type SourceStatus,
  type UploadConfirmResponse,
  type UploadEstimate,
} from '../model/types';

/**
 * Ключи запросов для источников.
 *
 * После confirm/delete инвалидируем list данного ассистента.
 * После confirm НЕ инвалидируем сам ingestion-запрос — у него отдельная жизнь
 * через polling.
 */
export const sourceQueryKeys = {
  all: ['sources'] as const,
  list: (assistantId: string) => [...sourceQueryKeys.all, 'list', assistantId] as const,
  ingestion: (sourceId: string) => [...sourceQueryKeys.all, 'ingestion', sourceId] as const,
  scrapeEstimate: (sourceId: string) =>
    [...sourceQueryKeys.all, 'scrape', 'estimate', sourceId] as const,
  scrapeProgress: (sourceId: string) =>
    [...sourceQueryKeys.all, 'scrape', 'progress', sourceId] as const,
  billingSummary: (sourceId: string) =>
    [...sourceQueryKeys.all, 'billing-summary', sourceId] as const,
  pages: (sourceId: string) => [...sourceQueryKeys.all, 'pages', sourceId] as const,
};

/** GET /knowledge/sources?assistant_id=... */
export function useSources(assistantId: string | undefined) {
  return useQuery<SourceListItem[], Error>({
    queryKey: sourceQueryKeys.list(assistantId ?? ''),
    queryFn: () => sourceApi.list(assistantId!),
    enabled: !!assistantId,
    staleTime: 10_000,
  });
}

/**
 * Polling статуса конкретного источника.
 *
 * Останавливаемся когда статус терминальный (success/partial_success/fail).
 * Период — 2 секунды (рекомендация из спеки).
 */
export function useIngestionStatus(sourceId: string | undefined) {
  return useQuery<IngestionStatusResponse, Error>({
    queryKey: sourceQueryKeys.ingestion(sourceId ?? ''),
    queryFn: () => sourceApi.getIngestionStatus(sourceId!),
    enabled: !!sourceId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return 2000;
      return isTerminalStatus(status) ? false : 2000;
    },
    retry: (failureCount, error) => {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Фаза 1: upload файла. Возвращает оценку + source_id. */
export function useUploadSourceMutation() {
  return useMutation<UploadEstimate, Error, { assistant_id: string; file: File }>({
    mutationFn: (payload) => sourceApi.upload(payload),
  });
}

/** Фаза 2: confirm. */
export function useConfirmSourceMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<
    UploadConfirmResponse,
    Error,
    { sourceId: string; process_images: boolean }
  >({
    mutationFn: ({ sourceId, process_images }) =>
      sourceApi.confirm(sourceId, { process_images }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sourceQueryKeys.list(assistantId) });
    },
  });
}

/** DELETE /sources/{id}. */
export function useDeleteSourceMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (sourceId) => sourceApi.delete(sourceId),
    onSuccess: (_void, sourceId) => {
      qc.removeQueries({ queryKey: sourceQueryKeys.ingestion(sourceId) });
      qc.removeQueries({ queryKey: sourceQueryKeys.pages(sourceId) });
      qc.invalidateQueries({ queryKey: sourceQueryKeys.list(assistantId) });
    },
  });
}

// ===========================================================================
//  Парсер сайтов (M-web1) — мутации и поллинги
// ===========================================================================

export function useStartScrapeEstimateMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<
    ScrapeEstimateStart,
    Error,
    { assistant_id: string; url: string; process_images: boolean }
  >({
    mutationFn: (payload) => sourceApi.startScrapeEstimate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sourceQueryKeys.list(assistantId) });
    },
  });
}

export function useScrapeEstimate(sourceId: string | undefined) {
  return useQuery<ScrapeEstimateResult, Error>({
    queryKey: sourceQueryKeys.scrapeEstimate(sourceId ?? ''),
    queryFn: () => sourceApi.getScrapeEstimate(sourceId!),
    enabled: !!sourceId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || status === 'in_progress') return 2500;
      return false;
    },
    retry: (failureCount, error) => {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) return false;
      return failureCount < 2;
    },
    staleTime: 0,
  });
}

export function useConfirmScrapeMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<ScrapeConfirmResponse, Error, { source_id: string }>({
    mutationFn: (payload) => sourceApi.confirmScrape(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sourceQueryKeys.list(assistantId) });
    },
  });
}

export function useScrapeProgress(sourceId: string | undefined) {
  return useQuery<ScrapeProgress, Error>({
    queryKey: sourceQueryKeys.scrapeProgress(sourceId ?? ''),
    queryFn: () => sourceApi.getScrapeProgress(sourceId!),
    enabled: !!sourceId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2500;
      if (data.vision_status === 'vision_done') return false;
      if (data.source_status === 'fail') return false;
      return 2500;
    },
    retry: (failureCount, error) => {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) return false;
      return failureCount < 2;
    },
    staleTime: 0,
  });
}

export function useBillingSummary(sourceId: string | undefined, enabled = true) {
  return useQuery<BillingSummary, Error>({
    queryKey: sourceQueryKeys.billingSummary(sourceId ?? ''),
    queryFn: () => sourceApi.getBillingSummary(sourceId!),
    enabled: !!sourceId && enabled,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Polling списка обработанных страниц веб-источника (P4-fix).
 *
 * Используется ТОЛЬКО для веб-источников. Для PDF/DOCX ответ всегда пустой
 * (source_url = NULL в чанках), но запрос валиден — не блокируем по типу.
 * Это упрощает компонент-потребитель: он сам решает показывать или нет.
 *
 * Поллинг:
 *  - Пока статус нетерминальный — раз в 3 сек (страницы добавляются по
 *    мере обработки, чаще не надо)
 *  - После терминального — один последний запрос для финального списка,
 *    дальше не поллим
 *
 * Параметр `assistantStatus` — текущий статус источника. Когда меняется
 * — polling может включиться/выключиться.
 */
export function useSourcePages(
  sourceId: string | undefined,
  sourceStatus: SourceStatus | undefined,
) {
  return useQuery<SourcePagesResponse, Error>({
    queryKey: sourceQueryKeys.pages(sourceId ?? ''),
    queryFn: () => sourceApi.getSourcePages(sourceId!),
    enabled: !!sourceId,
    refetchInterval: () => {
      if (!sourceStatus) return 3000;
      return isTerminalStatus(sourceStatus) ? false : 3000;
    },
    retry: (failureCount, error) => {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      // 404 = эндпоинта нет (если бэк ещё не задеплоен P4-fix). Не ретраим.
      if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) return false;
      return failureCount < 2;
    },
    staleTime: 0,
  });
}
